import * as fs from 'fs';
import * as path from 'path';
import { ConsoleLogger } from '../core/types';
import { generateConfig } from './generateConfig.js';

/**
 * 自动配置工具
 * 为项目生成 copilot-instructions.md 配置文件
 * 
 * v2.0.0: 移除 mcp.json 配置逻辑（由 VS Code 插件通过 npm 包自动配置）
 *         此工具现在只负责生成 copilot-instructions.md
 * v1.2.0: 新增自动生成项目 copilot-instructions.md
 */
export async function autoSetup(args: {
    workspacePath?: string;
    generateInstructions?: boolean; // 是否生成 copilot-instructions.md（默认 true）
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    
    try {
        // 确定工作区路径
        const workspacePath = args.workspacePath || process.cwd();
        
        if (!fs.existsSync(workspacePath)) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `工作区路径不存在: ${workspacePath}`
                    }, null, 2)
                }]
            };
        }

        const results = {
            workspacePath,
            steps: [] as Array<{ step: string; status: string; detail?: string }>,
            warnings: [] as string[]
        };

        logger.log('🚀 开始为项目生成 copilot-instructions.md...');

        // 检查并生成 copilot-instructions.md
        const generateInstructions = args.generateInstructions !== false; // 默认 true
        const instructionsPath = path.join(workspacePath, '.github', 'copilot-instructions.md');
        const hasExistingInstructions = fs.existsSync(instructionsPath);
        
        if (generateInstructions) {
            if (hasExistingInstructions) {
                results.steps.push({ 
                    step: '检测到已有 copilot-instructions.md', 
                    status: 'skip',
                    detail: '保留现有配置，不自动覆盖'
                });
            } else {
                logger.log('🔍 未检测到配置文件，开始分析项目...');
                
                try {
                    // 使用 generate_config 的 autoMatch 模式（它内部会处理所有逻辑）
                    const configResult = await generateConfig({
                        projectPath: workspacePath,
                        autoMatch: true,
                        updateMode: 'merge'
                    });
                    
                    const configContent = configResult.content[0];
                    if (configContent.type === 'text') {
                        const configData = JSON.parse(configContent.text);
                        
                        if (configData.success) {
                            const agentIds = configData.agents?.map((a: { id?: string; title?: string }) => a.id || a.title) || [];
                            results.steps.push({ 
                                step: '✅ 自动生成 copilot-instructions.md', 
                                status: 'success',
                                detail: `应用了 ${agentIds.length} 个 Agents: ${agentIds.join(', ')}`
                            });
                        } else {
                            results.warnings.push(`⚠️ 配置生成失败: ${configData.error || '未知错误'}`);
                            results.warnings.push('💡 建议：手动调用 generate_config 工具并指定 agentIds');
                            results.warnings.push('   例如: generate_config({ projectPath: "' + workspacePath + '", agentIds: ["vue3", "i18n"] })');
                        }
                    }
                } catch (error) {
                    results.warnings.push(`⚠️ 自动生成配置失败: ${error instanceof Error ? error.message : String(error)}`);
                    results.warnings.push('💡 你可以稍后手动运行以下命令：');
                    results.warnings.push('   1. analyze_project({ projectPath: "' + workspacePath + '" })');
                    results.warnings.push('   2. generate_config({ projectPath: "' + workspacePath + '", autoMatch: true })');
                }
            }
        } else {
            results.steps.push({ step: '跳过 copilot-instructions.md 生成', status: 'skip' });
        }

        logger.log('✅ 配置完成！');

        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    success: true,
                    message: '🎉 已为项目生成 copilot-instructions.md',
                    ...results,
                    nextSteps: [
                        '1. 确保 MCP 服务器已启动（通过 VS Code 插件自动配置）',
                        '2. 打开 GitHub Copilot Chat',
                        '3. 开始使用：Copilot 会自动应用项目规范',
                        '4. 高级用法：尝试说"获取 Vue 3 相关规范"'
                    ]
                }, null, 2)
            }]
        };
    } catch (error) {
        logger.error(`配置失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}
